import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, DollarSign, Calendar, Repeat } from 'lucide-react';

function Home() {
  // Date range state
  const today = new Date();
  const thisYear = today.getFullYear();
  const thisMonth = today.getMonth() + 1;
  const [startYear, setStartYear] = useState(thisYear);
  const [startMonth, setStartMonth] = useState(1);
  const [endYear, setEndYear] = useState(thisYear);
  const [endMonth, setEndMonth] = useState(thisMonth);

  const [statistics, setStatistics] = useState({
    totalProjects: 0,
    ongoingProjects: 0,
    completedProjects: 0,
    totalRevenue: 0,
    projectRevenue: 0,
    recurringRevenue: 0,
    activeRecurringContracts: 0,
    pendingAmount: 0,
    thisMonthRevenue: 0,
    thisWeekDeadlines: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Years for dropdown (from 2022 to current year)
  const years = [];
  for (let y = 2022; y <= thisYear; y++) years.push(y);
  const months = [1,2,3,4,5,6,7,8,9,10,11,12];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [startYear, startMonth, endYear, endMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Compose start and end date strings
      const start = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
      // Get last day of end month
      const lastDay = new Date(endYear, endMonth, 0).getDate();
      const end = `${endYear}-${String(endMonth).padStart(2, '0')}-${lastDay}`;
      
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const urlStats = `${baseUrl}/api/statistics?start=${start}&end=${end}`;
      const urlRecent = `${baseUrl}/api/recent-projects?start=${start}&end=${end}`;
      
      const [statsResponse, projectsResponse] = await Promise.all([
        fetch(urlStats),
        fetch(urlRecent)
      ]);
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStatistics(statsData);
      }
      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json();
        setRecentProjects(projectsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (setter) => (e) => {
    setter(Number(e.target.value));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case '진행중':
        return 'status-ongoing';
      case '납품':
        return 'status-completed';
      default:
        return 'status-default';
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>홈</h1>
        <p>Loop Dimension Finance Manager 대시보드</p>
      </div>

      {/* Date Range Selector */}
      <div className="date-range-selector">
        <label>기간 선택:</label>
        <select 
          value={startYear} 
          onChange={handleDateChange(setStartYear)}
        >
          {years.map(y => <option key={y} value={y}>{y}년</option>)}
        </select>
        <select 
          value={startMonth} 
          onChange={handleDateChange(setStartMonth)}
        >
          {months.map(m => <option key={m} value={m}>{m}월</option>)}
        </select>
        <span style={{ fontWeight: 500 }}>~</span>
        <select 
          value={endYear} 
          onChange={handleDateChange(setEndYear)}
        >
          {years.map(y => <option key={y} value={y}>{y}년</option>)}
        </select>
        <select 
          value={endMonth} 
          onChange={handleDateChange(setEndMonth)}
        >
          {months.map(m => <option key={m} value={m}>{m}월</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>데이터를 불러오는 중...</p>
        </div>
      ) : (
        <>
          <div className="dashboard-grid">
            <div className="dashboard-card">
              <div className="card-icon">
                <BarChart3 size={24} />
              </div>
              <div className="card-content">
                <h3>총 프로젝트</h3>
                <p className="card-value">{statistics.totalProjects}</p>
                <p className="card-description">
                  진행중: {statistics.ongoingProjects}개, 완료: {statistics.completedProjects}개
                </p>
              </div>
            </div>
            <div className="dashboard-card">
              <div className="card-icon">
                <TrendingUp size={24} />
              </div>
              <div className="card-content">
                <h3>총 수익</h3>
                <p className="card-value">{formatCurrency(statistics.totalRevenue)}</p>
                <p className="card-description">
                  이번 달: {formatCurrency(statistics.thisMonthRevenue)}
                </p>
                <p className="card-description">
                  프로젝트: {formatCurrency(statistics.projectRevenue)} · 정기 계약: {formatCurrency(statistics.recurringRevenue)}
                </p>
              </div>
            </div>
            <div className="dashboard-card">
              <div className="card-icon">
                <DollarSign size={24} />
              </div>
              <div className="card-content">
                <h3>미수금</h3>
                <p className="card-value">{formatCurrency(statistics.pendingAmount)}</p>
                <p className="card-description">
                  {statistics.ongoingProjects}개 프로젝트
                </p>
              </div>
            </div>
            <div className="dashboard-card">
              <div className="card-icon">
                <Calendar size={24} />
              </div>
              <div className="card-content">
                <h3>이번 주 마감</h3>
                <p className="card-value">{statistics.thisWeekDeadlines}</p>
                <p className="card-description">프로젝트 마감 예정</p>
              </div>
            </div>
            <div className="dashboard-card">
              <div className="card-icon">
                <Repeat size={24} />
              </div>
              <div className="card-content">
                <h3>정기 계약 수익</h3>
                <p className="card-value">{formatCurrency(statistics.recurringRevenue)}</p>
                <p className="card-description">
                  진행중 {statistics.activeRecurringContracts}건 · 선택 기간 누적
                </p>
              </div>
            </div>
          </div>

          <div className="recent-projects">
            <h2>최근 프로젝트</h2>
            <div className="project-list">
              {recentProjects.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  <p>최근 프로젝트가 없습니다.</p>
                </div>
              ) : (
                recentProjects.map((project) => (
                  <div key={project.id} className="project-item">
                    <div className="project-info">
                      <h4>{project.name}</h4>
                      <p>
                        {project.client} • 
                        <span className={`status-badge ${getStatusBadgeClass(project.status)}`}>
                          {project.status}
                        </span>
                      </p>
                    </div>
                    <div className="project-amount">
                      <span className="amount">{formatCurrency(project.amount)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Home; 